import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Dish } from "@/api/Dishes/DishesAPI.model";
import type { DishReference } from "@/hooks/useDishReference";
import { getTranslation } from "@/helpers/getTranslation";
import {
  useFilterActions,
  useFilterMode,
  useFilterSearchQuery,
  useFilterSelection,
  type FilterDimension,
} from "@/store/useDishFiltersStore";

export interface FilterOption {
  id: string;
  label: string;
  iconUrl?: string;
}

export interface FilterGroup {
  key: FilterDimension;
  label: string;
  options: FilterOption[];
  selected: string[];
}

/**
 * Dish filtering, ported from the waiter app's useDishFilters.
 *
 * Four attribute dimensions plus a free-text search over the name and
 * description. A dish's allergens are its own plus every allergen its
 * ingredients carry — the same union the details view shows, which matters
 * because on real data dishes rarely declare allergens directly.
 */
export const useDishFilters = (dishes: Dish[], reference: DishReference) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const selected = useFilterSelection();
  const mode = useFilterMode();
  const searchQuery = useFilterSearchQuery();
  const { toggle, setMode, setSearchQuery, clearAll } = useFilterActions();

  const {
    menuType: menuTypeIds,
    diet: dietIds,
    spice: spiceIds,
    allergens: allergenIds,
  } = selected;

  const toOptions = (
    items: { id: string; translations: { languageCode: string; name: string }[] }[],
  ): FilterOption[] =>
    items
      .map((item) => ({
        id: item.id,
        label: getTranslation(item.translations, language),
      }))
      .filter((option) => option.id && option.label);

  // Groups with nothing to offer are dropped, so the trigger disappears
  // entirely when a restaurant has no reference data at all.
  const filterGroups = useMemo<FilterGroup[]>(
    () =>
      (
        [
          {
            key: "menuType" as const,
            label: t("order.filters.menuType"),
            options: toOptions(reference.menuTypes),
            selected: menuTypeIds,
          },
          {
            key: "diet" as const,
            label: t("order.filters.diet"),
            options: toOptions(reference.diets),
            selected: dietIds,
          },
          {
            key: "spice" as const,
            label: t("order.filters.spice"),
            options: toOptions(reference.spiceLevels),
            selected: spiceIds,
          },
          {
            key: "allergens" as const,
            label: t("order.filters.allergens"),
            options: reference.allergens
              .map((allergen) => ({
                id: allergen.id,
                label: getTranslation(allergen.translations, language),
                iconUrl: allergen.iconUrl,
              }))
              .filter((option) => option.id && option.label),
            selected: allergenIds,
          },
        ] satisfies FilterGroup[]
      ).filter((group) => group.options.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reference, language, menuTypeIds, dietIds, spiceIds, allergenIds, t],
  );

  /**
   * A dimension the guest cannot see cannot filter.
   *
   * The menu type group is hidden inside a single menu (it could only take
   * dishes away there), and any group whose reference list failed to load
   * disappears too. A selection left behind in one of those would keep hiding
   * dishes with nothing on screen to explain why.
   */
  const activeIds = useMemo(() => {
    const shown = new Set(filterGroups.map((group) => group.key));
    const pick = (key: FilterDimension, ids: string[]) =>
      shown.has(key) ? ids : [];

    return {
      menuType: pick("menuType", menuTypeIds),
      diet: pick("diet", dietIds),
      spice: pick("spice", spiceIds),
      allergens: pick("allergens", allergenIds),
    };
  }, [filterGroups, menuTypeIds, dietIds, spiceIds, allergenIds]);

  /** ingredientId → allergenIds, since a dish's ingredient refs are id-only. */
  const ingredientAllergens = useMemo(() => {
    const map = new Map<string, string[]>();
    reference.ingredients.forEach((ingredient) =>
      map.set(ingredient.id, ingredient.allergenIds ?? []),
    );
    return map;
  }, [reference.ingredients]);

  const activeFilterCount =
    activeIds.menuType.length +
    activeIds.diet.length +
    activeIds.spice.length +
    activeIds.allergens.length;

  const visibleDishes = useMemo(() => {
    const {
      menuType: menuTypeIds,
      diet: dietIds,
      spice: spiceIds,
      allergens: allergenIds,
    } = activeIds;
    const query = searchQuery.trim().toLowerCase();
    const hasAttributeFilter = activeFilterCount > 0;

    if (!query && !hasAttributeFilter) return dishes;

    const allergensOf = (dish: Dish) => {
      const set = new Set(dish.allergenIds ?? []);
      const ingredientIds = [
        ...(dish.ingredients ?? []).map((item) => item.id),
        ...(dish.ingredientsByDishSizeId ?? []).flatMap((group) =>
          (group.ingredients ?? []).map((item) => item.id),
        ),
      ];
      ingredientIds.forEach((id) =>
        (ingredientAllergens.get(id) ?? []).forEach((allergenId) =>
          set.add(allergenId),
        ),
      );
      return set;
    };

    const hitMenuType = (dish: Dish) =>
      (dish.dishMenuTypeIds ?? []).some((id) => menuTypeIds.includes(id));
    const hitDiet = (dish: Dish) =>
      dish.dietId != null && dietIds.includes(dish.dietId);
    const hitSpice = (dish: Dish) =>
      dish.spiceLevelId != null && spiceIds.includes(dish.spiceLevelId);
    const hitAllergen = (dish: Dish) => {
      const set = allergensOf(dish);
      return allergenIds.some((id) => set.has(id));
    };

    const matchesQuery = (dish: Dish) => {
      if (!query) return true;
      return dish.translations.some(
        (translation) =>
          translation.name?.toLowerCase().includes(query) ||
          translation.description?.toLowerCase().includes(query),
      );
    };

    return dishes.filter((dish) => {
      if (!matchesQuery(dish)) return false;
      if (!hasAttributeFilter) return true;

      if (mode === "include") {
        // OR within a dimension, AND across the active ones.
        return (
          (menuTypeIds.length === 0 || hitMenuType(dish)) &&
          (dietIds.length === 0 || hitDiet(dish)) &&
          (spiceIds.length === 0 || hitSpice(dish)) &&
          (allergenIds.length === 0 || hitAllergen(dish))
        );
      }

      // exclude: drop the dish if it matches anything that was selected.
      return !(
        (menuTypeIds.length > 0 && hitMenuType(dish)) ||
        (dietIds.length > 0 && hitDiet(dish)) ||
        (spiceIds.length > 0 && hitSpice(dish)) ||
        (allergenIds.length > 0 && hitAllergen(dish))
      );
    });
  }, [
    dishes,
    searchQuery,
    activeFilterCount,
    activeIds,
    ingredientAllergens,
    mode,
  ]);

  return {
    filterGroups,
    toggle,
    mode,
    setMode,
    searchQuery,
    setSearchQuery,
    clearAll,
    activeFilterCount,
    hasActiveFilters: activeFilterCount > 0 || searchQuery.trim().length > 0,
    visibleDishes,
  };
};
